import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { recoveryApi, ApiError } from '../services/recoveryApi';
import {
    CASE_EVENT_TYPE_OPTIONS,
    CaseEventType,
    type CaseEvent,
    type SelectOption,
} from '../types/LegalCase';
import type { ErrorField } from '../types/ErrorField';

interface AddCaseEventValue {
    eventType: CaseEventType;
    eventDate: string;
    text: string;
    sheetReference: string;
}

// Gerencia o andamento processual de um caso: lista (timeline), registra e remove. Fetch
// próprio pelo id do caso, para a seção atualizar sem recarregar o page-view inteiro. Mesmo
// padrão do useLegalCaseParties (seededCaseRef re-seed por caso, opções vindas do page-form).
export default function useLegalCaseEvents(caseId: string | null, initialEvents?: CaseEvent[]) {
    const [events, setEvents] = useState<CaseEvent[]>(initialEvents || []);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorFields, setErrorFields] = useState<ErrorField[]>([]);
    const [typeOptions, setTypeOptions] = useState<SelectOption[]>(CASE_EVENT_TYPE_OPTIONS);
    // initialEvents chega como referência nova a cada render (o painel passa detail.events),
    // então fica fora das deps do efeito de reset — o reset ocorre só quando o caso muda.
    const seededCaseRef = useRef<string | null>(null);

    const fetchEvents = useCallback(async () => {
        if (!caseId) return;
        setIsLoading(true);
        try {
            const result = await recoveryApi.getCaseEvents(caseId);
            setEvents(result.items || []);
        } catch {
            // silencioso — a seção já mostra os andamentos que vieram no page-view
        } finally {
            setIsLoading(false);
        }
    }, [caseId]);

    useEffect(() => {
        if (seededCaseRef.current === caseId) return;
        seededCaseRef.current = caseId;
        setEvents(initialEvents || []);
        setErrorFields([]);
        // initialEvents fora das deps de propósito (referência nova a cada render).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseId]);

    useEffect(() => {
        const loadTypes = async () => {
            try {
                const opts = await recoveryApi.getLegalCaseFormOptions();
                if (opts.caseEventTypes?.length) setTypeOptions(opts.caseEventTypes);
            } catch {
                // fallback local já preenche
            }
        };
        loadTypes();
    }, []);

    const clearErrors = () => setErrorFields([]);

    const addEvent = async (data: AddCaseEventValue): Promise<boolean> => {
        if (!caseId) return false;
        setIsSaving(true);
        setErrorFields([]);
        try {
            await recoveryApi.addCaseEvent(caseId, {
                eventType: data.eventType || CaseEventType.OTHER,
                eventDate: data.eventDate || undefined,
                text: data.text,
                sheetReference: data.sheetReference || undefined,
            });
            await fetchEvents();
            toast.success('Andamento registrado');
            return true;
        } catch (e: any) {
            if (e instanceof ApiError && e.status === 422 && e.data?.errors) {
                toast.error(e.data.message || 'Dados inválidos. Verifique os campos.');
                setErrorFields(e.data.errors);
                return false;
            }
            if (e instanceof ApiError && e.status === 404) {
                toast.error(e.data?.message || 'Caso não encontrado');
                return false;
            }
            toast.error(e?.message || 'Erro ao registrar andamento');
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const removeEvent = async (eventId: string) => {
        if (!caseId) return;
        setIsSaving(true);
        try {
            await recoveryApi.removeCaseEvent(caseId, eventId);
            await fetchEvents();
            toast.success('Andamento removido');
        } catch (e: any) {
            toast.error(e?.data?.message || e?.message || 'Erro ao remover andamento');
        } finally {
            setIsSaving(false);
        }
    };

    return {
        events, isLoading, isSaving, errorFields, typeOptions,
        addEvent, removeEvent, clearErrors,
    };
}

export type { AddCaseEventValue };
